import { Heart, MessageCircle, Send, Bookmark, MoreHorizontal } from 'lucide-react';
import { useSocialStore, type Post } from '@/store/socialStore';
import { cn } from '@/lib/utils';
import { useState } from 'react';

export function PostCard({ post }: { post: Post }) {
    const { likedPostIds, bookmarkedPostIds, commentsByPost, likePost, bookmarkPost, addComment } = useSocialStore();
    const [commentText, setCommentText] = useState('');
    const [showComments, setShowComments] = useState(false);

    const isLiked = likedPostIds.has(post.id);
    const isBookmarked = bookmarkedPostIds.has(post.id);
    const postComments = commentsByPost[post.id] || [];

    const handleCommentSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!commentText.trim()) return;
        addComment(post.id, commentText.trim(), 'Me');
        setCommentText('');
        setShowComments(true);
    };

    return (
        <div className="bg-transparent border-b border-white/10 pb-5 mb-5 last:border-0 md:palaestra-card md:pb-0 md:mb-6">
            {/* Header */}
            <div className="flex items-center justify-between py-3 px-0 md:px-4">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-gold-500 to-bg-surface2 p-[1.5px]">
                        <div className="w-full h-full rounded-full bg-bg border border-bg overflow-hidden flex items-center justify-center text-white">
                            {post.authorAvatar || <span className="text-[10px] font-bold">{post.authorName.charAt(0)}</span>}
                        </div>
                    </div>
                    <div>
                        <div className="flex items-center gap-1">
                            <span className="text-sm font-bold text-white hover:text-text-secondary cursor-pointer transition-colors">
                                {post.authorName}
                            </span>
                            {post.authorRole && <span className="text-[10px] font-bold text-gold-500 bg-gold-500/10 px-1.5 py-0.5 rounded tracking-widest uppercase">{post.authorRole}</span>}
                            <span className="text-text-muted mx-1">•</span>
                            <span className="text-xs text-text-muted font-medium">{post.timestamp}</span>
                        </div>
                    </div>
                </div>
                <button className="text-white hover:text-text-secondary transition-colors">
                    <MoreHorizontal size={20} />
                </button>
            </div>

            {/* Content Text (If any, mostly for MVP we use placeholder image if none) */}
            <div className="relative aspect-square md:aspect-[4/5] bg-bg-surface overflow-hidden group">
                {/* Fallback pattern if no image */}
                <div className="absolute inset-0 bg-gradient-to-br from-bg-surface2 to-bg border-y border-white/5 flex items-center justify-center p-8 text-center text-white font-black text-xl leading-relaxed">
                    "{post.content}"
                </div>
            </div>

            {/* Actions */}
            <div className="py-3 px-0 md:px-4">
                <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-4">
                        <button onClick={() => likePost(post.id)} className="group active:scale-90 transition-transform">
                            <Heart size={24} className={cn("transition-colors", isLiked ? "fill-red-500 text-red-500" : "text-white group-hover:text-text-secondary")} />
                        </button>
                        <button onClick={() => setShowComments(!showComments)} className="group active:scale-90 transition-transform">
                            <MessageCircle size={24} className="text-white group-hover:text-text-secondary transition-colors" />
                        </button>
                        <button className="group active:scale-90 transition-transform">
                            <Send size={24} className="text-white group-hover:text-text-secondary transition-colors" />
                        </button>
                    </div>
                    <button onClick={() => bookmarkPost(post.id)} className="group active:scale-90 transition-transform">
                        <Bookmark size={24} className={cn("transition-colors", isBookmarked ? "fill-white text-white" : "text-white group-hover:text-text-secondary")} />
                    </button>
                </div>

                {/* Likes */}
                <div className="font-bold text-sm text-white mb-2 cursor-pointer">{post.likes.toLocaleString()} likes</div>

                {/* Main Content / Caption */}
                <div className="space-y-1">
                    <p className="text-sm text-text-primary">
                        <span className="font-bold text-white mr-2 cursor-pointer hover:underline">{post.authorName}</span>
                        {post.content}
                    </p>
                </div>

                {postComments.length > 0 && (
                    <button onClick={() => setShowComments(!showComments)} className="block text-text-muted text-sm mt-3 font-medium hover:text-text-secondary transition-colors">
                        {showComments ? "Hide comments" : `View all ${postComments.length} comments`}
                    </button>
                )}

                {showComments && postComments.length > 0 && (
                    <div className="mt-2 space-y-2">
                        {postComments.map(c => (
                            <div key={c.id} className="text-sm">
                                <span className="font-bold text-white mr-2">{c.authorName}</span>
                                <span className="text-text-primary">{c.content}</span>
                            </div>
                        ))}
                    </div>
                )}

                <form onSubmit={handleCommentSubmit} className="mt-3 flex items-center justify-between border-b border-white/10 pb-3 md:border-0 md:pb-0">
                    <input
                        type="text"
                        value={commentText}
                        onChange={e => setCommentText(e.target.value)}
                        placeholder="Add a comment..."
                        className="bg-transparent text-sm w-full focus:outline-none placeholder:text-text-muted text-white"
                    />
                    <button
                        type="submit"
                        disabled={!commentText.trim()}
                        className="text-blue-400 font-bold text-sm hover:text-white transition-colors disabled:opacity-50"
                    >
                        Post
                    </button>
                </form>
            </div>
        </div>
    );
}
